import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkle, Check, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate } from "@/lib/plan";

const PERKS = [
  "Unlimited photo analyses per day",
  "GPT-4o vision (more detailed) + Gemini",
  "Unlimited 30-day history view",
  "CSV export for your care team",
];

export default function UpgradePanel({ usage, onUpdated }) {
  const [loading, setLoading] = useState(false);
  if (!usage) return null;
  const { plan, price_usd, advanced_days_per_purchase } = usage;
  const isAdvanced = plan.tier === "advanced";
  const inTrial = plan.in_trial;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/plans/checkout", {
        origin_url: window.location.origin,
        plan: "advanced",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="card-organic p-6 relative overflow-hidden" data-testid="upgrade-panel">
      <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-[#C76E4B]/10 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-[#C76E4B] flex items-center justify-center">
            <Sparkle size={16} weight="fill" className="text-white" />
          </span>
          <span className="label-eyebrow">{isAdvanced ? (inTrial ? "Trial active" : "Advanced") : "Upgrade"}</span>
        </div>

        <h3 className="font-serif-display text-3xl text-[#1A2421] mt-3 tracking-tight">
          {isAdvanced ? "You're on Advanced" : "Tally Advanced"}
        </h3>

        {!isAdvanced && (
          <p className="text-sm text-[#5C6B62] mt-2">
            Unlock unlimited AI carb analysis for diabetes management.
          </p>
        )}

        {isAdvanced && inTrial && plan.trial_ends_at && (
          <p className="text-sm text-[#5C6B62] mt-2" data-testid="trial-status">
            Free trial ends <span className="text-[#1A2421] font-medium">{formatDate(plan.trial_ends_at)}</span>. Subscribe to keep Advanced features after that.
          </p>
        )}

        {isAdvanced && !inTrial && plan.advanced_until && (
          <p className="text-sm text-[#5C6B62] mt-2" data-testid="advanced-status">
            Active until <span className="text-[#1A2421] font-medium">{formatDate(plan.advanced_until)}</span>. Renews for ${price_usd} every {advanced_days_per_purchase} days.
          </p>
        )}

        {!isAdvanced && (
          <div className="mt-4 flex items-baseline gap-1.5">
            <span className="font-serif-display text-4xl text-[#2B422F]">${price_usd}</span>
            <span className="text-sm text-[#5C6B62]">/ month</span>
          </div>
        )}

        <ul className="mt-5 space-y-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-[#1A2421]">
              <span className="w-5 h-5 rounded-full bg-[#2B422F]/10 flex items-center justify-center mt-0.5 shrink-0">
                <Check size={11} weight="bold" className="text-[#2B422F]" />
              </span>
              {p}
            </li>
          ))}
        </ul>

        {(!isAdvanced || !inTrial) && (
          <button
            onClick={startCheckout}
            disabled={loading}
            className="btn-accent w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="upgrade-checkout-button"
          >
            {loading ? "Opening checkout…" : isAdvanced ? `Renew · $${price_usd}` : `Subscribe · $${price_usd}/mo`}
            <ArrowRight size={16} weight="bold" />
          </button>
        )}
        {isAdvanced && inTrial && (
          <button
            onClick={startCheckout}
            disabled={loading}
            className="btn-primary w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="upgrade-checkout-button"
          >
            {loading ? "Opening checkout…" : `Lock in Advanced · $${price_usd}/mo`}
          </button>
        )}

        <p className="mt-3 text-[11px] text-[#5C6B62] text-center">
          Cancel anytime · Test mode (no real charge)
        </p>
      </div>
    </div>
  );
}
